// The card's own primitives (PLAN-UI §28.3, U6): the surface, the header, the
// readout rows, the controls row and the hover card's body.
// What the *sections* dress is `sectionStyles.ts`.

import {
  keyframes,
  motion,
  Panel,
  REDUCED_MOTION,
  STILL,
  styled
} from "../kit";
import { Swatch } from "../unitRows";

/** The mock's entry: a fade and a 6px rise. Off under reduced motion. */
const enter = keyframes({
  from: { opacity: 0, transform: "translateY(-6px)" },
  to: { opacity: 1, transform: "none" }
});

/**
 * How tall the card may get before it scrolls.
 *
 * It sits at `top: 110`, so this leaves 30px under it. The tall case is a
 * foundry with its forge list, and the alternative to a cap is a card whose last section is under the bottom of the
 * screen with nothing saying so.
 */
const MAX_HEIGHT = "calc(100vh - 140px)";

/**
 * The card. The mock's `#insp`: 240px at `left:100px; top:110px`, entry in
 * opacity and a 6px rise, on the chrome rung. The surface is the kit's
 * `Panel`, so the glass moves with the sheet.
 *
 * The `left` is the card's own, and nothing overrides it any more: the dock
 * died at U8, so the free area's left edge is the screen's.
 *
 * **240 still, with the sections in it.** The one thing that did not fit was
 * the five rank tiles at the rail's fixed width; sharing the row was the
 * cheaper answer than 20px of card nothing else wanted.
 */
export const Card = styled(Panel)(({ theme }) => ({
  position: "absolute",
  left: 100,
  top: 110,
  width: 240,
  maxHeight: MAX_HEIGHT,
  overflowY: "auto",
  padding: "12px 15px",
  boxSizing: "border-box",
  zIndex: theme.kit.z.chrome,
  // **It takes the pointer and it now has controls**, which is the U6 review's
  // change: the bar came in here rather than floating over the board. That is
  // safe because the card is off the board's own squares — it is a fixed
  // rectangle at the free area's left edge, not an overlay on the selection —
  // so a click it takes was never a click on a square.
  animation: `${enter} ${theme.kit.motion.duration.draw}ms ${theme.kit.motion.easeBounce} both`,
  [REDUCED_MOTION]: STILL
}));

/** The heading — the mock's `#insp h3`. */
export const Name = styled("h3")(({ theme }) => ({
  margin: "0 0 2px",
  font: `400 18px ${theme.kit.font.heading}`,
  letterSpacing: ".4px"
}));

/**
 * The material in front of a name, where the subject *is* a material — ore,
 * scrap. `Swatch` is a bare 8px box, and an 18px heading needs it off the
 * baseline.
 */
export const NameSwatch = styled(Swatch)({
  display: "inline-block",
  verticalAlign: "middle",
  marginRight: 6
});

/** `d4 · yours` — the mock's `#insp h3 span`. */
export const Where = styled("span")(({ theme }) => ({
  font: `600 10px ${theme.kit.font.display}`,
  letterSpacing: ".08em",
  textTransform: "uppercase",
  color: theme.kit.color.inkDim,
  marginLeft: 6
}));

export const Pips = styled("div")(({ theme }) => ({
  fontSize: 12,
  lineHeight: 1.45,
  color: theme.kit.color.inkDim,
  marginBottom: 6
}));

export const Pip = styled("div")({ display: "flex", gap: 5, alignItems: "baseline" });

/**
 * A pip's glyph, in its own tone.
 *
 * Three states and two colours: `working` and `ready` are both the accent —
 * they were one colour in `PieceOverlays` too — and `waiting` is the accent
 * sunk into the dim ink, which reads as "this is a job that has not started".
 * Classes rather than a prop, so emotion mints the rule once.
 */
export const PipGlyph = styled("span")(({ theme }) => ({
  fontSize: 13,
  lineHeight: 1.3,
  flexShrink: 0,
  color: theme.kit.color.brand,
  "&.pip-waiting": {
    color: `color-mix(in srgb, ${theme.kit.color.brand} 45%, ${theme.kit.color.inkDim})`
  }
}));

/** The name, carrying the rule on its hover card — the shelf's `Legend` mark. */
export const Rule = styled("span")(({ theme }) => ({
  textDecoration: "underline dotted",
  textDecorationColor: theme.kit.color.inkDim,
  textUnderlineOffset: 3,
  cursor: "help"
}));

/** The mock's `.irow`: a rule above, the label left, the number right. */
export const Row = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 8,
  padding: "3.5px 0",
  borderTop: `1px solid ${theme.kit.color.line}`,
  fontSize: 12.5,
  lineHeight: 1.35,
  "&.stack": { display: "block" },
  // A swatch and the words it labels are one phrase; space-between is for a
  // label at one end and its number at the other.
  "&.start": { justifyContent: "flex-start" }
}));

/**
 * The one number a card is about — a vein's seam, a heap. Big because it is the
 * readout, and there is no caption saying so.
 */
export const Headline = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "baseline",
  gap: 6,
  margin: "4px 0 2px",
  font: `700 26px ${theme.kit.font.display}`,
  color: theme.kit.color.ink
}));

/** The small half of a `Headline` — this tile's own charge, beside the seam's. */
export const Aside = styled("span")(({ theme }) => ({
  font: `600 11px ${theme.kit.font.display}`,
  letterSpacing: ".06em",
  color: theme.kit.color.inkDim
}));

/** The mock's `#insp .irow span:last-child`. */
export const Value = styled("span")(({ theme }) => ({
  font: `700 12px ${theme.kit.font.display}`,
  color: theme.kit.color.ink,
  textAlign: "right"
}));

/** A hover card's sentence. `HoverCard` styles a bare string; this is a node. */
export const CardBody = styled("div")(({ theme }) => ({
  fontFamily: theme.kit.font.body,
  fontSize: 12,
  lineHeight: 1.45,
  color: theme.kit.color.inkDim
}));

/**
 * The controls row — the mock's `#act`, brought inside the card.
 *
 * It **wraps**: a store carries Take-it-down, a king on a heap carries three
 * free actions and a posting, and a card 240px wide cannot hold either on one
 * line. The rule above it is the same divider every readout row takes, so the
 * controls read as the last block rather than as a raft.
 */
export const Controls = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: 5,
  marginTop: 7,
  paddingTop: 7,
  borderTop: `1px solid ${theme.kit.color.line}`
}));

/** One section's block — the popover's `#ctx` divider, in the card. */
export const Block = styled("div")(({ theme }) => ({
  borderTop: `1px solid ${theme.kit.color.line}`,
  paddingTop: theme.kit.space.sm,
  marginTop: theme.kit.space.sm
}));

/**
 * One control. The mock's `#act button` minus its caption line — the state is
 * the caption now (designer ruling, U6 review), so this is one line of label.
 *
 * Not the kit's `Btn`: that is a one-line control with a hard drop and a press
 * compression, and this row wants the tile family's flat edge and armed wash so
 * a lit toggle here and a lit tile on the hotbar mean the same thing.
 */
export const Button = styled("button")(({ theme }) => {
  const c = theme.kit.color;
  return {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "4px 8px",
    background: `color-mix(in srgb, ${c.panelRaised} 85%, transparent)`,
    border: `2px solid ${c.line}`,
    borderRadius: theme.kit.radius.sm + 2,
    color: c.ink,
    fontFamily: theme.kit.font.body,
    fontWeight: 700,
    fontSize: 11.5,
    lineHeight: 1.2,
    cursor: "pointer",
    ...motion(["transform"], ["background", "border-color"]),

    "&:hover": { transform: "translateY(-2px)", borderColor: c.lineHi },
    "&:focus-visible": { outline: `2px solid ${c.brand}`, outlineOffset: 2 },

    // A lit toggle is the one orange thing in the row — `Tile`'s armed wash,
    // derived the same way rather than minted a second time.
    '&[aria-pressed="true"]': {
      background: `color-mix(in srgb, ${c.brand} 14%, ${c.ground})`,
      borderColor: c.brand,
      color: c.brand
    },
    // Same specificity as `&:hover` and after it, so it wins. Still hoverable:
    // the card is the whole point of an off control.
    '&[aria-disabled="true"]': {
      opacity: 0.35,
      cursor: "default",
      transform: "none",
      borderColor: c.line
    },

    "@media (pointer: coarse)": { minHeight: theme.kit.touchTarget }
  };
});

/**
 * A control's glyph, in the tone its state asks for.
 *
 * `ready` is the brand, because a coalition that can pay is the one thing
 * worth spotting from across the card; `short` is rust sunk into the dim ink
 * rather than rust itself, since a standing recipe that is not funded yet is
 * "not yet" and never a refusal — saving up is what the control is *for*, and
 * the loud colour would be telling the player off for using it; `unset` is the
 * dim ink. Classes, so emotion mints the rule once.
 */
export const Glyph = styled("span")(({ theme }) => ({
  fontSize: 13,
  lineHeight: 1,
  "&.ready": { color: theme.kit.color.brand },
  "&.short": {
    color: `color-mix(in srgb, ${theme.kit.color.rust} 55%, ${theme.kit.color.inkDim})`
  },
  "&.unset": { color: theme.kit.color.inkDim }
}));

export const Track = styled("div")(({ theme }) => ({
  height: 4,
  marginTop: 5,
  borderRadius: 2,
  overflow: "hidden",
  background: `color-mix(in srgb, ${theme.kit.color.ink} 12%, transparent)`
}));

export const Fill = styled("div")(({ theme }) => ({
  height: "100%",
  background: `color-mix(in srgb, ${theme.kit.color.brand} 45%, ${theme.kit.color.line})`,
  "&.ready": { background: theme.kit.color.brand }
}));

/** The card's last line, as plain prose. Read on a touch screen, so never a hover. */
export const Prose = styled("div")(({ theme }) => ({
  marginTop: 6,
  paddingTop: 5,
  borderTop: `1px solid ${theme.kit.color.line}`,
  fontSize: 11.5,
  lineHeight: 1.4,
  color: theme.kit.color.inkDim
}));

/** `Prose` as a hover trigger — the help cursor is the mark that there is more. */
export const Note = styled(Prose)({ cursor: "help" });
